import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  Typography,
  TextField,
} from "@mui/material";
import MUIDataTable from "mui-datatables";
import axios from "axios";
import Loader from "../../../components/loader/Loader";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import ExcelJS from "exceljs";
import { useTableHeight } from "../../../hooks/useTablePagination";

// ── Payload shapes this screen produces ─────────────────────────────────────
//   { from_date, to_date, region_id, delivery_mode, shop_id }
// region_id / shop_id are only included when actually selected (same
// convention as SalesReport's loadOrders/params building).

const DELIVERY_MODES = [
  { value: "ALL", label: "All" },
  { value: "Delivery", label: "Delivery" },
  { value: "Pick Up", label: "Pick Up" },
];

const formatDateInput = (d) => d.toISOString().slice(0, 10);

const getDefaultDateRange = () => {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { from: formatDateInput(first), to: formatDateInput(last) };
};

function CancelledOrderReport() {
  const [loader, setLoader] = useState(false);
  const [formData, setFormData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [deliveryCount, setDeliveryCount] = useState(0);
  const [pickupCount, setPickupCount] = useState(0);

  const defaultRange = getDefaultDateRange();
  const [fromDate, setFromDate] = useState(defaultRange.from);
  const [toDate, setToDate] = useState(defaultRange.to);

  // ── Same redux-driven region/shop pattern as SalesReport ──────────────────
  const loginType = sessionStorage.getItem("loginType") || "admin";
  const loggedInShopId = Number(sessionStorage.getItem("shop_id")) || 0;

  const [region, setRegion] = useState(""); // single region_id, same as SalesReport
  const [shop, setShop] = useState(""); // single shop_details_id, same as SalesReport
  const [deliveryMode, setDeliveryMode] = useState("ALL");

  const regions = useSelector((state) => state.counter.allRegionList);
  const allShops = useSelector((state) => state.counter.shopList);

  const [filteredShops, setFilteredShops] = useState([]);
  const [filteredRegions, setFilteredRegions] = useState([]);

  const tableHeight = useTableHeight();

  const isRegionDisabled = loginType === "shop";
  const isShopDisabled = loginType === "shop";

  // ── Lock region/shop for a shop-login user (mirrors SalesReport) ─────────
  useEffect(() => {
    if (loginType === "shop" && loggedInShopId) {
      setShop(loggedInShopId);
      const shopDetails = allShops.find(
        (s) => s.shop_details_id === loggedInShopId,
      );
      if (shopDetails && shopDetails.region_id) {
        setRegion(shopDetails.region_id);
      }
    }
  }, [loginType, loggedInShopId, allShops]);

  // ── Region options: only the shop-user's own region, else full list ──────
  useEffect(() => {
    if (loginType === "shop" && loggedInShopId) {
      const shopDetails = allShops.find(
        (s) => s.shop_details_id === loggedInShopId,
      );
      if (shopDetails && shopDetails.region_id) {
        const shopRegion = regions.filter(
          (r) => r.region_id === shopDetails.region_id,
        );
        setFilteredRegions(shopRegion);
      } else {
        setFilteredRegions([]);
      }
    } else {
      setFilteredRegions(regions);
    }
  }, [loginType, loggedInShopId, regions, allShops]);

  // ── Shop options: filtered by selected region, else full list ────────────
  useEffect(() => {
    if (loginType === "shop" && loggedInShopId) {
      const shopDetails = allShops.find(
        (s) => s.shop_details_id === loggedInShopId,
      );
      setFilteredShops(shopDetails ? [shopDetails] : []);
    } else if (region) {
      setFilteredShops(
        allShops.filter((s) => s.region_id === parseInt(region)),
      );
    } else {
      setFilteredShops(allShops);
    }
  }, [loginType, loggedInShopId, region, allShops]);

  // ── load whenever any filter changes ────────────────────────────────────
  useEffect(() => {
    if (!fromDate || !toDate) return;
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, region, shop, deliveryMode]);

  // ── build request body ──────────────────────────────────────────────────
  const buildRequestBody = () => {
    const body = {
      from_date: fromDate,
      to_date: toDate,
    };

    if (region) body.region_id = parseInt(region);
    if (shop) body.shop_id = parseInt(shop);
    if (deliveryMode !== "ALL") body.delivery_mode = deliveryMode;

    return body;
  };

  // ── API call ─────────────────────────────────────────────────────────
  const loadReport = () => {
    setLoader(true);

    axios({
      url: "/reports/cancelledOrderSummary",
      method: "post",
      data: buildRequestBody(),
    })
      .then((res) => {
        const payload = res?.data?.data || {};
        const summary = payload.summary || {};
        const arr = Array.isArray(payload.data) ? payload.data : [];

        setFormData(arr);
        setTotalCount(summary.total_cancelled_orders ?? arr.length);
        setDeliveryCount(
          summary.delivery_count ??
            arr.filter((r) => r.delivery_mode === "Delivery").length,
        );
        setPickupCount(
          summary.pickup_count ??
            arr.filter((r) => r.delivery_mode === "Pick Up").length,
        );
      })
      .catch(() => {
        setFormData([]);
        setTotalCount(0);
        setDeliveryCount(0);
        setPickupCount(0);
      })
      .finally(() => setLoader(false));
  };

  const formatIndianCurrency = (num) => {
    const n = parseFloat(num) || 0;
    return n.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const getRegionName = (id) =>
    regions.find((r) => r.region_id === id)?.city?.name || "-";

  const getShopName = (id) =>
    allShops.find((s) => s.shop_details_id === id)?.shop_name || "-";

  // ── Excel export ─────────────────────────────────────────────────────
  const handleCustomDownload = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Cancelled Order Report");

      const toNumber = (val) => {
        const n = parseFloat(val);
        return isNaN(n) ? 0 : n;
      };

      worksheet.columns = [
        { header: "Order ID", key: "order_id", width: 14 },
        { header: "Customer", key: "customer_name", width: 22 },
        { header: "Region", key: "region_name", width: 18 },
        { header: "Delivery Mode", key: "delivery_mode", width: 14 },
        { header: "Shop", key: "shop_name", width: 20 },
        { header: "Order Amount (₹)", key: "order_amount", width: 16 },
        { header: "Cancelled On", key: "cancelled_at", width: 16 },
        { header: "Cancel Reason", key: "cancel_reason", width: 28 },
      ];

      formData.forEach((m) => {
        worksheet.addRow({
          order_id: m.order_id ?? m.id ?? "-",
          customer_name: m.customer_name || m.billing_name || "-",
          region_name: m.region_name || m.region?.name || "-",
          delivery_mode: m.delivery_mode || "-",
          shop_name: m.shop_name || m.shop_orders?.shop?.shop_name || "-",
          order_amount: toNumber(m.order_amount ?? m.order_overall_totall),
          cancelled_at: m.cancelled_at || m.order_cancelled_at || "-",
          cancel_reason: m.cancel_reason || "-",
        });
      });

      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true };
      headerRow.alignment = { horizontal: "center", vertical: "middle" };
      headerRow.height = 20;

      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > 1) {
          row.eachCell((cell) => {
            cell.alignment = { horizontal: "center", vertical: "middle" };
          });
        }
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "Cancelled_Order_Report.xlsx";
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Excel error:", err);
      alert("Failed to generate Excel file.");
    }
  };

  const columns = [
    {
      name: "id",
      label: "S. No",
      options: {
        customBodyRenderLite: (dataIndex) => <p>{dataIndex + 1}</p>,
      },
    },
    {
      name: "order_id",
      label: "Order ID",
      options: {
        customBodyRenderLite: (i) => (
          <Typography variant="body2">
            {formData[i]?.order_id ?? formData[i]?.id ?? "-"}
          </Typography>
        ),
      },
    },
    {
      name: "customer_name",
      label: "Customer",
      options: {
        customBodyRenderLite: (i) => (
          <Typography variant="body2">
            {formData[i]?.customer_name || formData[i]?.billing_name || "-"}
          </Typography>
        ),
      },
    },
    {
      name: "region_name",
      label: "Region",
      options: {
        customBodyRenderLite: (i) => (
          <Typography variant="body2">
            {formData[i]?.region_name || formData[i]?.region?.name || "-"}
          </Typography>
        ),
      },
    },
    {
      name: "delivery_mode",
      label: "Delivery Mode",
      options: {
        customBodyRenderLite: (i) => {
          const mode = formData[i]?.delivery_mode;
          if (!mode) return <Typography variant="body2">-</Typography>;
          const isPickup = mode === "Pick Up";
          return (
            <Chip
              icon={
                isPickup ? (
                  <StorefrontOutlinedIcon sx={{ fontSize: 14 }} />
                ) : (
                  <LocalShippingOutlinedIcon sx={{ fontSize: 14 }} />
                )
              }
              label={mode}
              size="small"
              sx={{
                bgcolor: isPickup ? "#fff3e0" : "#e3f2fd",
                color: isPickup ? "#ed6c02" : "#1565c0",
                fontWeight: 600,
              }}
            />
          );
        },
      },
    },
    {
      name: "shop_name",
      label: "Shop",
      options: {
        customBodyRenderLite: (i) => (
          <Typography variant="body2">
            {formData[i]?.shop_name ||
              formData[i]?.shop_orders?.shop?.shop_name ||
              "-"}
          </Typography>
        ),
      },
    },
    {
      name: "order_amount",
      label: "Amount",
      options: {
        customBodyRenderLite: (i) => {
          const amount =
            formData[i]?.order_amount ?? formData[i]?.order_overall_totall;
          return (
            <Typography variant="body2">
              {amount ? `₹${formatIndianCurrency(amount)}` : "-"}
            </Typography>
          );
        },
      },
    },
    {
      name: "cancelled_at",
      label: "Cancelled On",
      options: {
        customBodyRenderLite: (i) => (
          <Typography variant="body2">
            {formData[i]?.cancelled_at ||
              formData[i]?.order_cancelled_at ||
              "-"}
          </Typography>
        ),
      },
    },
    {
      name: "cancel_reason",
      label: "Reason",
      options: {
        customBodyRenderLite: (i) => (
          <Typography variant="body2">
            {formData[i]?.cancel_reason || "-"}
          </Typography>
        ),
      },
    },
  ];

  const options = {
    filter: false,
    search: true,
    tableBodyHeight: tableHeight,
    selectableRows: "none",
    rowsPerPage: 12,
    count: formData.length,
    serverSide: false,
    print: false,
    viewColumns: false,
    download: true,
    onDownload: () => {
      handleCustomDownload();
      return false;
    },
    textLabels: {
      body: {
        noMatch: loader ? "Loading..." : "No records found",
      },
    },
  };

  return (
    <>
      {loader ? (
        <Loader />
      ) : (
        <>
          {/* ── Header ── */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 2,
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <h4 className="text-dark">Cancelled Order Report</h4>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Chip
                label={`${totalCount} cancelled`}
                color="error"
                size="small"
              />
              <Chip
                label={`${deliveryCount} delivery`}
                sx={{ bgcolor: "#e3f2fd", color: "#1565c0", fontWeight: 600 }}
                size="small"
              />
              <Chip
                label={`${pickupCount} pick up`}
                sx={{ bgcolor: "#fff3e0", color: "#ed6c02", fontWeight: 600 }}
                size="small"
              />
            </Box>
          </Box>

          {/* ── Filters ── */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* From Date */}
            <Grid item xs={12} sm={6} md={2.5}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From Date"
                InputLabelProps={{ shrink: true }}
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </Grid>

            {/* To Date */}
            <Grid item xs={12} sm={6} md={2.5}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To Date"
                InputLabelProps={{ shrink: true }}
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </Grid>

            {/* Region — same single-select redux pattern as SalesReport */}
            <Grid item xs={12} sm={6} md={2.3}>
              <FormControl fullWidth size="small" disabled={isRegionDisabled}>
                <InputLabel>Region</InputLabel>
                <Select
                  value={region}
                  label="Region"
                  onChange={(e) => {
                    setRegion(e.target.value);
                    if (loginType !== "shop") {
                      setShop("");
                    }
                  }}
                >
                  <MenuItem value="">All Regions</MenuItem>
                  {filteredRegions?.map((r) => (
                    <MenuItem key={r.region_id} value={r.region_id}>
                      {r.city?.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Shop — same redux pattern as SalesReport, filtered by region, always visible */}
            <Grid item xs={12} sm={6} md={2.2}>
              <FormControl fullWidth size="small" disabled={isShopDisabled}>
                <InputLabel>Shop</InputLabel>
                <Select
                  value={shop}
                  label="Shop"
                  onChange={(e) => setShop(e.target.value)}
                >
                  <MenuItem value="">All Shops</MenuItem>
                  {filteredShops.map((s) => (
                    <MenuItem key={s.shop_details_id} value={s.shop_details_id}>
                      {s.shop_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Delivery Mode */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Delivery Mode</InputLabel>
                <Select
                  value={deliveryMode}
                  label="Delivery Mode"
                  onChange={(e) => setDeliveryMode(e.target.value)}
                >
                  {DELIVERY_MODES.map((m) => (
                    <MenuItem key={m.value} value={m.value}>
                      {m.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* ── Main Content ── */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={12}>
              <MUIDataTable
                title="Cancelled Order Report"
                data={formData}
                columns={columns}
                options={options}
              />
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
}

export default CancelledOrderReport;